import React, { useContext, useEffect, useState } from 'react';
import { CompanyRelationService, IntegrationSettingsInput } from '../../../../../../../types/integration_settings';
import { IntegrationModalState } from '../../IntegrationModal';
import useMutation from '../../../../../../../hooks/useMutation';
import {
  DeleteIntegrationSettingsResponse,
  DeleteIntegrationSettingsVariables,
  IntegrationSettingsMutations,
} from '../../../../../../../graphql/integrationSettings.graphql';
import { IntegrationContext } from '../../../../../../../context/IntegrationContext';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../../components/Toast';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../../../types/translation/Translator';
import MicrosoftIntegrationConnectPane from './MicrosoftIntegrationConnectPane';
import MicrosoftIntegrationInfoPane from './MicrosoftIntegrationInfoPane';

interface MicrosoftIntegrationPaneProps {
  input: IntegrationSettingsInput;
  handleCancel: () => void;
}

export enum MicrosoftIntegrationPaneState {
  connect = 'connect',
  info = 'info',
}

export default function MicrosoftIntegrationPane({ input, handleCancel }: MicrosoftIntegrationPaneProps) {
  if (input.type !== CompanyRelationService.microsoft) return null;

  const { showToast } = useToast();
  const { integrationSettings, setIntegrationSettings } = useContext(IntegrationContext);

  const determineType = () => {
    if (!input.id) return MicrosoftIntegrationPaneState.connect;
    return IntegrationModalState.info;
  };

  const [state, setState] = useState(determineType());

  const [remove, { loading }] = useMutation<DeleteIntegrationSettingsResponse, DeleteIntegrationSettingsVariables>(
    IntegrationSettingsMutations.remove,
    {
      onCompleted: response => {
        integrationSettings.delete(response.deleteIntegrationSettings[0].id);
        setIntegrationSettings(new Map(integrationSettings));
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.integrationDeletedSuccessfullyToastTitle }),
        });
        handleCancel();
      },
    },
  );

  useEffect(() => {
    if (input.version) return;

    setState(determineType());
  }, [input]);

  const handleDelete = async () => {
    await remove({ variables: { settings: [input.forDelete()] } });
  };

  if (state === MicrosoftIntegrationPaneState.connect) {
    return <MicrosoftIntegrationConnectPane input={input} handleCancel={handleCancel} />;
  }

  if (state === MicrosoftIntegrationPaneState.info) {
    return (
      <MicrosoftIntegrationInfoPane
        input={input}
        loading={loading}
        handleCancel={handleCancel}
        handleDelete={handleDelete}
      />
    );
  }

  return <></>;
}
